import { useSelector, useDispatch } from 'react-redux';
import { useCallback } from 'react';
import {
  updateArrivalsNearCurrentLocation,
  updateArrivalsNearSelectedLocation,
  removeLocationArrivals,
} from '#/transit-tracker/queries';
import { useGetNearbyStopsQuery } from '#/services/developerApi';
import useCurrentLocation from '#/shared/hooks/useCurrentLocation';
import useCurrentQuery from '#/trip-planner/hooks/useCurrentQuery';
import useSelectedLocation from '#/shared/hooks/useSelectedLocation';

const useNearbyOrFavoriteArrivals = isCurrentLocation => {
  const {
    arrivalsTimestamp,
    favoriteArrivals,
    nearbyLocationArrivals,
    nearbyArrivals,
  } = useSelector(s => s.transitTracker);

  const { currentQuery } = useCurrentQuery();
  const { selectedLocation } = useSelectedLocation();
  const { currentLocation } = useCurrentLocation();

  const locationToUse = isCurrentLocation ? currentLocation : selectedLocation;
  const { maxWalkDistance } = currentQuery;

  const { data: nearbyStops } = useGetNearbyStopsQuery(
    {
      ll: `${locationToUse?.lat},${locationToUse?.lon}`,
      meters: maxWalkDistance,
    },
    { skip: !locationToUse?.lat || !locationToUse?.lon || !maxWalkDistance }
  );

  const dispatch = useDispatch();

  const refreshArrivals = useCallback(() => {
    if (isCurrentLocation) {
      dispatch(updateArrivalsNearCurrentLocation(currentLocation, nearbyStops));
    } else {
      dispatch(updateArrivalsNearSelectedLocation(nearbyStops));
    }
  }, [isCurrentLocation, dispatch, currentLocation, nearbyStops]);

  const clearLocationArrivals = useCallback(() => {
    dispatch(removeLocationArrivals());
  }, [dispatch]);

  return {
    // favorite arrivals are included because they're updated via the
    // same requests that update arrivals near the current location
    favoriteArrivals,

    nearbyArrivals,
    nearbyLocationArrivals,
    nearbyStops,
    arrivalsTimestamp,
    refreshArrivals,
    clearLocationArrivals,
  };
};

export default useNearbyOrFavoriteArrivals;
